/**
 * Helper functions to trigger prompts client-side.
 */

import { MiaPlaza } from "../../../Reinforced.Typings";
import I18Next from "../I18Next";
import { Notifier } from "./Shared/Notifier";
import type { IChoice, IPrompt, MaybeTextResource } from "./PromptTypes";
import pDefer from "p-defer";

/**
 * Represents an active prompt instance.
 *
 * Internally, a deferred promise pattern is used to resolve the promise later
 * in response to user interaction (e.g., when a choice is selected). While this
 * pattern is generally discouraged due to potential misuse, it is appropriate
 * here because resolution depends on external UI events. Only the resulting
 * Promise is exposed.
 *
 * The promise resolves only when a choice is selected. It is never rejected,
 * and calling `hide` does not resolve nor reject it, it simply removes the prompt
 * from the UI, preventing further interaction.
 *
 * @template T The type of the value returned when a choice is selected.
 */
export interface PromptInstance<T> {
	/** Resolves with the selected choice value. */
	promise: Promise<T>

	/** Hides or dismisses the prompt from the UI. */
	hide: () => void
}

/**
 * Displays a prompt with a single close button.
 *
 * The returned promise resolves when the user clicks the close button.
 */
export function prompt(message: MaybeTextResource): PromptInstance<unknown>;

/**
 * Displays a prompt with one button per provided choice.
 *
 * Each choice maps to a value of type `T`. The returned promise resolves
 * with the value of the selected choice.
 *
 * @template T The type of the value returned when a choice is selected.
 */
export function prompt<T>(message: MaybeTextResource, choices: Array<IChoice<T>>): PromptInstance<T>;
export function prompt<T>(message: MaybeTextResource, choices?: Array<IChoice<T>>): PromptInstance<T> {
	if (!choices || choices.length === 0) {
		choices = getDefaultPromptChoices();
	}

	const deferredPromise = pDefer<T>();

	const promptId = Notifier.show<IPrompt>({
		Template: "Prompt",
		choices: choices.map((choice) => ({
			label: choice.label,
			value: () => deferredPromise.resolve(choice.value),
		})),
		message,
	});

	const hide = () => Notifier.hide(promptId);

	return { promise: deferredPromise.promise, hide };
}

/**
 * Default close choice for prompts
 */
const getDefaultPromptChoices = <T>(): IChoice<T>[] => {
	return [{
		label: I18Next.tr("Prompt_Close"),
		// eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
		value: undefined as unknown as T,
	}];
};

/**
 * Show an Ok/Cancel dialog.
 * @returns a promise that resolves to true when Ok has been selected, otherwise to false.
 */
export function okCancel(message: MaybeTextResource): Promise<boolean> {
	return prompt(message, [
		{ label: I18Next.tr("OkCancel_Ok"), value: true },
		{ label: I18Next.tr("OkCancel_Cancel"), value: false },
	]).promise;
}

/**
 * Show a Yes/No dialog.
 * @returns a promise that resolves to the selected option.
 */
export function yesNo(message: MaybeTextResource): Promise<boolean> {
	return prompt(message, [
		{ label: I18Next.tr("YesNo_Yes"), value: true },
		{ label: I18Next.tr("YesNo_No"), value: false },
	]).promise;
}

export function errorPrompt(message: string): void {
	Notifier.show<MiaPlaza.Control.Vue.Notifications.ISessionMessage>({
		Message: message,
		Template: "SessionMessage",
		Type: MiaPlaza.Control.Vue.Notifications.SessionMessageType.Error,
	});
}

export function warningPrompt(message: string): void {
	Notifier.show<MiaPlaza.Control.Vue.Notifications.ISessionMessage>({
		Message: message,
		Template: "SessionMessage",
		Type: MiaPlaza.Control.Vue.Notifications.SessionMessageType.Warning,
	});
}

export function successPrompt(message: string): void {
	Notifier.show<MiaPlaza.Control.Vue.Notifications.ISessionMessage>({
		Message: message,
		Template: "SessionMessage",
		Type: MiaPlaza.Control.Vue.Notifications.SessionMessageType.Success,
	});
}

/**
 * Show an informational session message.
 *
 * @returns the identifier of the shown notification or an empty string when the notification
 * was not shown because the maximum amount of notifications is already on screen.
 */
export function infoPrompt(message: string): string {
	return Notifier.show<MiaPlaza.Control.Vue.Notifications.ISessionMessage>({
		Message: message,
		Template: "SessionMessage",
		Type: MiaPlaza.Control.Vue.Notifications.SessionMessageType.Info,
	});
}
